/** Lightweight rich-text editor — bold / italic / underline / bullet list.
 * Stores HTML. Accepts legacy plain-text values (converted on load). */
import { useEffect, useRef } from "react";
import { Bold, Italic, Underline, List } from "lucide-react";

const escapeHtml = (t) =>
  t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Content produced by this editor (or any HTML source): has tags or entities.
const isHtmlish = (v) => /<[a-z][\s\S]*>/i.test(v) || /&[a-z#0-9]+;/i.test(v);

// Legacy content is plain text — convert newlines so it round-trips cleanly.
const toHtml = (v) => {
  if (!v) return "";
  if (isHtmlish(v)) return v;
  return escapeHtml(v).replace(/\n/g, "<br>");
};

const TOOLS = [
  { cmd: "bold", icon: Bold, label: "Bold" },
  { cmd: "italic", icon: Italic, label: "Italic" },
  { cmd: "underline", icon: Underline, label: "Underline" },
  { cmd: "insertUnorderedList", icon: List, label: "Bullet list" },
];

export const RichTextEditor = ({ value, onChange, minHeight = 120, testid = "rich-text-editor" }) => {
  const ref = useRef(null);
  const lastEmitted = useRef(null);

  // Only rewrite the DOM when the value changed externally (e.g. dialog opened
  // with a different record) — never on our own keystrokes, or the cursor jumps.
  useEffect(() => {
    if (!ref.current) return;
    if (value === lastEmitted.current) return; // our own change echoed back via props
    if (document.activeElement === ref.current) return; // never clobber while typing
    const html = toHtml(value || "");
    if (html !== ref.current.innerHTML) {
      ref.current.innerHTML = html;
      lastEmitted.current = value ?? "";
    }
  }, [value]);

  const emit = () => {
    let html = ref.current?.innerHTML ?? "";
    // Fully-cleared editor: drop leftover empty wrappers (<ul><li><b><br>…)
    // so the next typed text doesn't silently inherit old formatting.
    if (!(ref.current?.textContent || "").trim()) {
      html = "";
      if (ref.current && ref.current.innerHTML !== "") ref.current.innerHTML = "";
    }
    lastEmitted.current = html;
    onChange(html);
  };

  const exec = (cmd) => {
    ref.current?.focus();
    // A collapsed caret can carry bold/italic state into a new list — clear it
    // so bullet items don't silently inherit formatting.
    if (cmd === "insertUnorderedList" && window.getSelection()?.isCollapsed) {
      ["bold", "italic", "underline"].forEach((c) => {
        if (document.queryCommandState(c)) document.execCommand(c, false, null);
      });
    }
    document.execCommand(cmd, false, null);
    emit();
  };

  return (
    <div className="bg-white border border-[#EBE3D2] rounded-md focus-within:ring-2 focus-within:ring-[#C28A2E]/40" data-testid={testid}>
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-[#EBE3D2] bg-[#FDFBF5] rounded-t-md">
        {TOOLS.map(({ cmd, icon: Icon, label }) => (
          <button
            key={cmd}
            type="button"
            title={label}
            aria-label={label}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec(cmd)}
            className="w-8 h-8 rounded-md flex items-center justify-center text-[#6B5944] hover:bg-[#C28A2E]/12 hover:text-[#5D3A1F] transition-colors"
            data-testid={`${testid}-${cmd === "insertUnorderedList" ? "bullet-list" : cmd}`}
          >
            <Icon size={15} strokeWidth={2} />
          </button>
        ))}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        className="rich-text px-3 py-2.5 text-sm text-[#2A1810] outline-none overflow-y-auto"
        style={{ minHeight, maxHeight: 420 }}
        data-testid={`${testid}-input`}
      />
    </div>
  );
};

export default RichTextEditor;
